/**
 * The single row of chrome under the editor. It is the only place in the panel
 * that reports state back to the user, so it stays at 12px and says as little
 * as it can get away with.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Settings2 } from 'lucide-react'
import { MAX_SCRATCHPAD_BYTES } from '@shared/scratchpad-limits'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { byteSize, relativeTime } from '@/lib/format'
import { useNotes, useSync } from '@/store/app-store'
import { ENDPOINT_PROBLEM_MESSAGE } from '@/sync/endpoint'
import type { SyncStatus } from '@/sync/engine'

/** Coarse enough to be invisible, fine enough that "just now" never lingers. */
const TICK_MS = 20_000

/**
 * A clock the row can re-render against. Without it "updated just now" would sit
 * there until the next keystroke, which is exactly when it stops being true.
 */
function useClock(): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  return now
}

const SAVE_LABEL = {
  idle: '',
  saving: 'saving...',
  saved: 'saved',
  // The one state the row must not let slide past: the note on screen is not
  // the note on disk, and it keeps retrying until it is.
  error: 'save failed — retrying',
} as const

interface SyncNotice {
  label: string
  /** The whole explanation, because the row has space for a word and no more. */
  detail: string
  tone: 'muted' | 'danger'
  /**
   * Whether pressing it does something, which is true of exactly one state.
   *
   * A Conflict is the only sync state the user can act on from here — every other
   * one clears itself, or is fixed in Settings — so it is the only one that becomes
   * a button. The rest stay words on purpose: a control that looks pressable and
   * is not is worse than no control.
   */
  actionable?: boolean
}

/**
 * What, if anything, the row says about sync.
 *
 * Silence is the answer for most of it: sync switched off is the default, and a
 * device that agrees with its Jot Server has nothing to report — a permanent
 * "synced" would be one more thing on screen for a scratchpad that is meant to be
 * a text field. Only a state the user might act on earns a word.
 */
function syncNotice(status: SyncStatus, now: number): SyncNotice | null {
  switch (status.kind) {
    case 'off':
    case 'agreed':
      return null

    case 'conflict':
      return {
        label: 'conflict',
        // `pushedBy` is asserted by whichever device pushed, so it is reported
        // rather than stated (ADR-0003).
        detail: `The server has a different version, saved ${relativeTime(status.record.updatedAt, now)} by a device calling itself "${status.record.pushedBy}". Nothing is lost and editing continues; sync stops pushing until you choose one.`,
        tone: 'danger',
        actionable: true,
      }

    // Waiting, which is not a Conflict and must not read like one: nothing has
    // changed on this device, nothing was recorded, and it clears itself. It names
    // what is being waited for and says that no text is at risk — and it promises
    // no pane, because the one that resolves a Conflict is the Conflict's own and
    // there is nothing here to resolve.
    //
    // What produces it moved with issue #6 and the sentence had to move with it. It
    // used to be the permanent state of a device whose Scratchpad was empty while the
    // server held a body, which pull now converges; it is now the seconds in which a
    // save is still going in and a pulled body may not be written across it. So the
    // sentence no longer claims this scratchpad is empty, and it says the wait is over
    // when the save lands rather than leaving the user to wonder what would end it.
    case 'waiting':
      return {
        label: 'server has text',
        detail: `The Jot Server holds a version saved ${relativeTime(status.record.updatedAt, now)} by a device calling itself "${status.record.pushedBy}". Jot has not copied it down because this device still has a save going in; it changes nothing here until that lands, and then it looks again.`,
        tone: 'muted',
      }

    // Sync is switched on and pointed at somewhere Jot will not go. Not "off":
    // the user asked for sync and is not getting it.
    case 'refused':
      return {
        label: 'check settings',
        detail: ENDPOINT_PROBLEM_MESSAGE[status.problem],
        tone: 'danger',
      }

    case 'unauthorized':
      return {
        label: 'sync rejected',
        detail: 'The Jot Server did not accept this token. Check it in Settings.',
        tone: 'danger',
      }

    case 'unreachable':
      return {
        label: 'sync offline',
        detail: 'The Jot Server could not be reached. The next save tries again.',
        tone: 'muted',
      }

    case 'rejected':
      return {
        label: 'sync failed',
        detail: `The Jot Server answered ${status.httpStatus}.`,
        tone: 'danger',
      }

    case 'malformed':
      return {
        label: 'sync failed',
        detail: 'The Jot Server answered something Jot could not read.',
        tone: 'danger',
      }

    // Not folded into `malformed`, because it is the one failure here that never
    // clears itself: a Scratchpad over the cap stays over it, so this device will
    // not pair and will not push until something changes on the server. The
    // sentence therefore has to name a size and a remedy rather than a symptom.
    case 'oversized':
      return {
        label: 'scratchpad too large',
        detail: `The Jot Server's scratchpad is larger than the ${byteSize(MAX_SCRATCHPAD_BYTES)} Jot will read, so nothing was pushed and nothing here changed. Shorten it from a device that is already paired.`,
        tone: 'danger',
      }

    // The same never-clearing shape pointing the other way, and the reason it is not
    // a bare `413`: this scratchpad only grows, so the row has to say which limit was
    // met and what the two ways out are. Which limit it was decides the sentence —
    // the shared ceiling is Jot's own and can only be met by shortening, while a
    // server's configured cap is a number its operator can raise.
    //
    // Neither branch names the limit, and the ceiling one deliberately stopped: two
    // numbers a decimal apart render as one. `byteSize` rounds to a tenth of a MiB,
    // so every size from 8323073 to about 8336179 bytes read "This scratchpad takes
    // 7.9 MiB to send, more than the 7.9 MiB any Jot Server accepts" — a strict
    // inequality between two identical numbers, at exactly the sizes that meet the
    // ceiling. The size and the remedy are what the sentence is for; the cap is
    // neither, because the only way past it is to shorten, which the sentence says.
    case 'too-large':
      return {
        label: 'scratchpad too large',
        detail:
          status.limit === null
            ? `This scratchpad takes ${byteSize(status.bytes)} to send and this Jot Server refused it as too large. Nothing was pushed and nothing here changed. Shorten the scratchpad, or raise JOT_MAX_BODY_BYTES on the server.`
            : `This scratchpad takes ${byteSize(status.bytes)} to send, more than any Jot Server accepts, so nothing was sent and nothing here changed. Shorten it.`,
        tone: 'danger',
      }

    case 'unavailable':
      return {
        label: 'sync waiting',
        detail:
          status.what === 'scratchpad'
            ? 'Jot could not reach the scratchpad in storage, so it pushed nothing.'
            : 'Jot could not record what it last agreed with the server, so it pushed nothing.',
        tone: 'muted',
      }
  }
}

/**
 * Storage could not be read, so nothing is open and nothing has been written.
 *
 * It takes the row's one slot ahead of anything sync has to say, because it is the
 * cause of it: with no Scratchpad readable there is nothing to push, and "sync
 * waiting" would describe the symptom while the row stayed silent about the text the
 * user is typing into memory.
 */
const UNREADABLE_NOTICE: SyncNotice = {
  label: 'scratchpad unreadable',
  detail:
    'Jot could not read the scratchpad from storage, so it has opened nothing and written nothing — the copy on disk is untouched. What you type is held here and goes in as soon as the read succeeds, which Jot keeps retrying, but it is not saved yet: leave Jot before then and it is lost.',
  tone: 'danger',
}

export function StatusBar({
  onOpenSettings,
  onOpenConflict,
}: {
  onOpenSettings: () => void
  /** Open the Resolution pane. Reached only from the Conflict badge. */
  onOpenConflict: () => void
}): ReactNode {
  const { note, saveState, unreadable } = useNotes()
  const syncStatus = useSync()
  const now = useClock()
  const notice = unreadable ? UNREADABLE_NOTICE : syncNotice(syncStatus, now)

  return (
    <footer className="flex h-7 shrink-0 items-center justify-between gap-3 border-t bg-surface px-3 text-[12px]">
      <div className="flex min-w-0 items-center gap-2">
        {note ? (
          <span className="truncate text-fg-muted">
            updated {relativeTime(note.updatedAt, now)}
          </span>
        ) : null}
        <span
          // Polite rather than assertive: autosave is reassurance, not an alert.
          aria-live="polite"
          className={cn(
            'shrink-0',
            saveState === 'error'
              ? 'text-danger'
              : saveState === 'saving'
                ? 'text-fg-faint'
                : 'text-fg-muted',
          )}
        >
          {SAVE_LABEL[saveState]}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {/* A button for the one state that has something behind it, a word for the
            rest — see `SyncNotice.actionable`. The detail is on the element either
            way, so a 720px panel does not have to fit the sentence. */}
        {notice?.actionable ? (
          <Button
            variant="ghost"
            title={notice.detail}
            className="no-drag max-w-[180px] truncate text-danger hover:text-danger"
            onClick={onOpenConflict}
          >
            {notice.label}
          </Button>
        ) : notice ? (
          <span
            aria-live="polite"
            title={notice.detail}
            className={cn(
              'max-w-[180px] truncate',
              notice.tone === 'danger' ? 'text-danger' : 'text-fg-muted',
            )}
          >
            {notice.label}
          </span>
        ) : null}

        <Button
          variant="ghost"
          size="icon"
          className="no-drag"
          aria-label="Settings"
          onClick={onOpenSettings}
        >
          <Settings2 size={14} strokeWidth={1.5} />
        </Button>
      </div>
    </footer>
  )
}
